import React, { FC, Fragment, ReactNode, useMemo } from 'react';
import { nanoid } from 'nanoid';
import MuiDrawer, { DrawerProps as MuiDrawerProps } from '@mui/material/Drawer';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';

import { Actions } from './components/actions';
import { Content } from './components/content';
import { LightThemeProvider } from 'style/theme';

export type DrawerProps = Omit<
    MuiDrawerProps,
    | 'anchor'
    | 'BackdropComponent'
    | 'BackdropProps'
    | 'children'
    | 'classes'
    | 'closeAfterTransition'
    | 'components'
    | 'componentsProps'
    | 'container'
    | 'disableAutoFocus'
    | 'disableBackdropClick'
    | 'disableEnforceFocus'
    | 'disableEscapeKeyDown'
    | 'disablePortal'
    | 'disableRestoreFocus'
    | 'disableScrollLock'
    | 'elevation'
    | 'fullScreen'
    | 'fullWidth'
    | 'hideBackdrop'
    | 'keepMounted'
    | 'manager'
    | 'maxWidth'
    | 'ModalProps'
    | 'onBackdropClick'
    | 'onEnter'
    | 'onEntered'
    | 'onEntering'
    | 'onEscapeKeyDown'
    | 'onExit'
    | 'onExited'
    | 'onExiting'
    | 'onRendered'
    | 'PaperComponent'
    | 'PaperProps'
    | 'scroll'
    | 'size'
    | 'SlideProps'
    | 'TransitionComponent'
    | 'transitionDuration'
    | 'TransitionProps'
> & {
    children: ReactNode;
    title?: string;
};

type DrawerSubTypes = {
    /**
     * The action area is intended for one or more `Button` components. The area is padded, the content floats to the right, and the elements are spaced automatically.
     */
    Actions: typeof Actions;
    /**
     * A simple wrapper that provides consistent spacing around the main content of all Drawers. If the `children` prop is of type `string`, it will be wrapped with a `p` tag with no additional spacing, otherwise it will be rendered as-is. Simple textual content consisting of a single paragraph should be passed directly.
     */
    Content: typeof Content;
};

/**
 * Implements [Drawer](https://mui.com/components/Drawers/) from **Material-UI**.
 */
export const Drawer: FC<DrawerProps> & DrawerSubTypes = ({
    children,
    title,
    ...rest
}) => {
    const theme = useTheme();

    const titleId = useMemo(() => nanoid(), []);

    const content = (
        <Fragment>
            {title && <DialogTitle id={titleId}>{title}</DialogTitle>}
            {children}
        </Fragment>
    );

    return (
        <MuiDrawer aria-labelledby={titleId} {...rest}>
            {theme.palette.mode === 'light' ? (
                content
            ) : (
                <LightThemeProvider>{content}</LightThemeProvider>
            )}
        </MuiDrawer>
    );
};

Drawer.Actions = Actions;
Drawer.Content = Content;
